import React, { useEffect, useState } from 'react';
import VideoPlayer from './video/VideoPlayer';
import VideoDetails from './video/VideoDetails';
import EloScoreDisplay from './video/EloScoreDisplay';
import TipAnimation from './video/TipAnimation';
import { MOCK_VIDEOS } from '@/lib/mock-data';
import { useIsMobile } from '@/hooks/use-mobile';
import { useAuth } from '@/contexts/AuthContext';
import { supabase } from '@/integrations/supabase/client';
import { useVideoNavigation } from '@/hooks/use-video-navigation';
import { useVideoInteractions } from '@/hooks/use-video-interactions';
import { toast } from 'sonner';

const VideoFeed: React.FC = () => {
  const [eloScore, setEloScore] = useState<number | null>(null);
  const [videoViews, setVideoViews] = useState<{ [key: string]: number }>(
    () => MOCK_VIDEOS.reduce((acc, video) => ({
      ...acc,
      [video.id]: parseInt(video.views.replace('k', '000'))
    }), {})
  );

  const isMobile = useIsMobile();
  const { user } = useAuth();
  const { currentVideo, feedRef } = useVideoNavigation(MOCK_VIDEOS);
  const {
    tipAmount,
    setTipAmount,
    showTipAnimation,
    handleLike,
    handleTip
  } = useVideoInteractions();

  useEffect(() => {
    const fetchEloScore = async () => {
      if (!user?.id) return;

      try {
        const { data, error } = await supabase
          .from('profiles')
          .select('elo_rating')
          .eq('id', user.id)
          .maybeSingle();

        if (error) {
          console.error("Error fetching ELO score:", error);
          setEloScore(0);
          return;
        }

        setEloScore(data?.elo_rating ?? 0);
      } catch (error) {
        console.error("Error in fetchEloScore:", error);
        setEloScore(0);
      }
    };

    fetchEloScore();
  }, [user?.id]);

  const currentVideoData = MOCK_VIDEOS[currentVideo];

  if (!currentVideoData) {
    return <div>No video available</div>;
  }

  const formatViews = (views: number): string => {
    if (views >= 1000000) {
      return `${(views / 1000000).toFixed(1)}M`;
    } else if (views >= 1000) {
      return `${(views / 1000).toFixed(1)}k`;
    }
    return views.toString();
  };

  return (
    <div 
      ref={feedRef}
      className="fixed inset-0 w-full h-full overflow-hidden"
      style={{ touchAction: 'pan-y' }}
    >
      <div 
        className="relative h-full w-full transition-transform duration-300 ease-out"
        style={{ transform: `translateY(-${currentVideo * 100}%)` }}
      >
        {MOCK_VIDEOS.map((video, index) => (
          <div 
            key={video.id}
            className="absolute top-0 left-0 w-full h-full"
            style={{ transform: `translateY(${index * 100}%)` }}
          >
            <div className="relative h-full rounded-xl overflow-hidden">
              {isMobile && <EloScoreDisplay eloScore={eloScore} />}

              <VideoPlayer
                videoUrl={video.url}
                videoId={video.id}
                onTip={handleTip}
                tipAmount={tipAmount}
                setTipAmount={setTipAmount}
              />
              
              <TipAnimation show={showTipAnimation} amount={tipAmount} />
            </div>
          </div>
        ))}
      </div>

      <VideoDetails
        title={currentVideoData.title}
        description={currentVideoData.description}
        creator={currentVideoData.creator}
        views={formatViews(videoViews[currentVideoData.id])}
        music={currentVideoData.music}
        isTrending={currentVideoData.isTrending}
        isPartnered={currentVideoData.isPartnered}
        videoId={currentVideoData.id}
      />
    </div>
  );
};

export default VideoFeed;